import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Muffler (Act II, ACT2-BIBLE.md §8) — a Great Hush echo: it carries a small
 * bubble of silence, and inside it your weapons go dumb.
 *
 * The same inversion the Hush is built on — a place that mutes *you* rather
 * than one that hurts you — shrunk to something that walks toward you. It
 * cannot kill you on its own; what it does is arrive while something else is
 * killing you. The correct answer (back off, then shoot it from outside the
 * bubble) is the same repositioning instinct the boss will demand.
 *
 * `hushing` is set per-frame for the renderer; the actual mute is a per-frame
 * write to `player.muted`, which MinionWaveSystem clears at the top of each
 * tick so the flag can never stick after the Muffler dies or the player leaves.
 */
export class Muffler {
  constructor(eventBus, {
    x, y, radius = 6, hp, moveSpeed, contactDamage, hushRadius = 42,
  }) {
    this.eventBus = eventBus;
    this.type = 'muffler';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.hushRadius = hushRadius;
    this.hushing = false; // true while the player is inside the bubble — drives its tell
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player) {
    // A dead Muffler must not get one last mute in: MinionWaveSystem updates
    // every minion and only *then* sweeps the dead ones, so without this the
    // frame it died would still silence the player.
    if (this.isDead) return;
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    this.hushing = dist <= this.hushRadius + player.radius;
    if (this.hushing) player.muted = true;

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.hushing = false;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
